import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { listRequestSchema } from './common/list.js';
import { paginated } from './common/pagination.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

export const CAMPAIGN_STAFF_STATUSES = ['active', 'passive'] as const;
export type CampaignStaffStatus = (typeof CAMPAIGN_STAFF_STATUSES)[number];

/**
 * Whether an assignment is in force now, decided by the server from its status
 * and its window: `scheduled` has not started, `ended` is past its end, and
 * only `active` is in force — the one §12.6 derives authority from.
 */
export const CAMPAIGN_STAFF_STATES = ['active', 'scheduled', 'ended', 'passive'] as const;
export type CampaignStaffState = (typeof CAMPAIGN_STAFF_STATES)[number];

/**
 * A staff user's role — the season managers' roles, which moved here when a
 * campaign's people became one layer (docs/decisions/0095). A season leaves
 * `draft` only when each active yurt's in-force staff assignment names an
 * active `primary`; several primaries are allowed.
 */
export const CAMPAIGN_STAFF_ROLES = ['primary', 'backup'] as const;
export type CampaignStaffRole = (typeof CAMPAIGN_STAFF_ROLES)[number];
export const campaignStaffRoleSchema = z.enum(CAMPAIGN_STAFF_ROLES);

/** How many users one assignment names, and how many institutions one create staffs at once. */
export const CAMPAIGN_STAFF_MAX_USERS = 50;
export const CAMPAIGN_STAFF_MAX_INSTITUTIONS = 50;

const timestampSchema = z.iso.datetime({ offset: true });

/** The users of one assignment, each with a role; a user left without one is a backup. */
const staffUsersSchema = z
  .array(z.object({ userId: z.uuid(), role: campaignStaffRoleSchema }))
  .max(CAMPAIGN_STAFF_MAX_USERS);

export type CampaignStaffUserEntry = z.infer<typeof staffUsersSchema>[number];

/**
 * The institution a user sits at — shown beside them, never a condition: any
 * user may be staffed on any institution's work (docs/decisions/0094).
 */
const seatSchema = z.object({ id: z.uuid(), name: z.string(), code: z.string() });

export const campaignStaffUserSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  email: z.string(),
  isActive: z.boolean(),
  role: campaignStaffRoleSchema,
  institution: seatSchema,
});

export type CampaignStaffUser = z.infer<typeof campaignStaffUserSchema>;

export const campaignStaffAssignmentSchema = z.object({
  id: z.uuid(),
  campaign: z.object({ id: z.uuid(), name: z.string() }),
  institution: z.object({ id: z.uuid(), name: z.string(), code: z.string() }),
  status: z.enum(CAMPAIGN_STAFF_STATUSES),
  state: z.enum(CAMPAIGN_STAFF_STATES),
  startsAt: z.string(),
  endsAt: z.string().nullable(),
  users: z.array(campaignStaffUserSchema),
  /** Null for a row migration 0125 folded from a season's managers — nobody created it. */
  createdBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type CampaignStaffAssignment = z.infer<typeof campaignStaffAssignmentSchema>;

/**
 * `staffed` / `unstaffed` — docs/decisions/0095's S3 amendment. **Unstaffed**
 * is a campaign × institution named by an active yurt of a non-closed season
 * of a non-archived campaign, with no staff row covering it that is in force
 * (active, started, not ended) with at least one active user: no row, a
 * passive row, an ended row, or a row with no active users.
 */
export const CAMPAIGN_STAFF_COVERAGE = ['staffed', 'unstaffed'] as const;
export type CampaignStaffCoverage = (typeof CAMPAIGN_STAFF_COVERAGE)[number];

/**
 * A campaign × institution pair `/campaign-staff` can point at but that no
 * staff row currently covers — no `id`, because there is nothing yet to edit
 * or remove, only to staff. Appears in the list only under `coverage:
 * 'unstaffed'`.
 */
export const campaignStaffGapSchema = z.object({
  kind: z.literal('gap'),
  campaign: z.object({ id: z.uuid(), name: z.string() }),
  institution: z.object({ id: z.uuid(), name: z.string(), code: z.string() }),
});

export type CampaignStaffGap = z.infer<typeof campaignStaffGapSchema>;

/**
 * One row of the overview list: a real assignment, or a synthetic gap a
 * caller may staff. `kind` tells the two apart, since a gap carries no `id`.
 */
export const campaignStaffListItemSchema = z.discriminatedUnion('kind', [
  campaignStaffAssignmentSchema.extend({ kind: z.literal('assignment') }),
  campaignStaffGapSchema,
]);

export type CampaignStaffListItem = z.infer<typeof campaignStaffListItemSchema>;

/** What an assigned user reads of their own assignment, and nothing about their colleagues. */
export const myCampaignAssignmentSchema = z.object({
  id: z.uuid(),
  campaign: z.object({ id: z.uuid(), name: z.string() }),
  institution: z.object({ id: z.uuid(), name: z.string() }),
  state: z.enum(CAMPAIGN_STAFF_STATES),
  startsAt: z.string(),
  endsAt: z.string().nullable(),
  assignedAt: z.string(),
});

export type MyCampaignAssignment = z.infer<typeof myCampaignAssignmentSchema>;

export const campaignStaffOptionsSchema = z.object({
  campaigns: z.array(z.object({ id: z.uuid(), name: z.string(), categoryName: z.string() })),
  institutions: z.array(
    z.object({ id: z.uuid(), name: z.string(), code: z.string(), countryName: z.string() }),
  ),
});

export type CampaignStaffOptions = z.infer<typeof campaignStaffOptionsSchema>;

export const campaignStaffUserOptionSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  email: z.string(),
  institution: seatSchema,
  /** False for a user holding permissions the caller does not: naming them is refused. */
  manageable: z.boolean(),
});

export type CampaignStaffUserOption = z.infer<typeof campaignStaffUserOptionSchema>;

const windowIsOrdered = (value: { startsAt?: string | undefined; endsAt?: string | null }) =>
  !value.startsAt || !value.endsAt || Date.parse(value.endsAt) > Date.parse(value.startsAt);

/**
 * A removal, or a deactivation, of an assignment whose campaign still has
 * pending or queued donations to be carried out at its institution is refused
 * `CONFLICT` / `CAMPAIGN_STAFF_PENDING_DONATIONS` with the count, unless the
 * request says it was shown the count and goes ahead.
 */
const confirmationSchema = z.boolean().default(false);

/**
 * Campaign staff assignments — docs/decisions/0089,
 * docs/plans/12-operations-and-access.md §12.5. **Institution scope**: a
 * manager reads and writes the assignments of institutions inside their scope,
 * and a campaign they may read. `campaignStaff.view` reads, `campaignStaff.manage`
 * writes; `mine` needs a session and nothing else.
 */
export const campaignStaffContract = {
  /**
   * `userId` narrows to assignments naming that user. `coverage` turns the
   * list into the bulk overview's coverage question: `unstaffed` answers
   * which of the caller's campaign × institution pairs have nobody covering
   * them, mixing real rows (a passive or non-covering assignment) with
   * synthetic gap rows (no assignment at all) — both highlighted the same
   * way and both offered an assignment action. Left unset, the list is
   * exactly what it always was: real assignment rows, unfiltered by
   * coverage.
   */
  list: oc
    .route({
      method: 'GET',
      path: '/campaign-staff-assignments',
      summary: 'Campaign staff assignments in the caller’s scope',
    })
    .errors(guarded)
    .input(
      listRequestSchema.extend({
        campaignId: z.uuid().optional(),
        institutionId: z.uuid().optional(),
        status: z.enum(CAMPAIGN_STAFF_STATUSES).optional(),
        userId: z.uuid().optional(),
        coverage: z.enum(CAMPAIGN_STAFF_COVERAGE).optional(),
      }),
    )
    .output(paginated(campaignStaffListItemSchema)),

  get: oc
    .route({ method: 'GET', path: '/campaign-staff-assignments/{id}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ assignment: campaignStaffAssignmentSchema })),

  /** The campaigns and institutions the screen's filters and its create dialog offer. */
  options: oc
    .route({ method: 'GET', path: '/campaign-staff-assignment-options' })
    .errors(guarded)
    .input(z.object({}))
    .output(campaignStaffOptionsSchema),

  /**
   * The active users the caller can see, wherever they sit — any of them may
   * be named on any assignment the caller places (docs/decisions/0094).
   */
  userOptions: oc
    .route({ method: 'GET', path: '/campaign-staff-assignment-user-options' })
    .errors(guarded)
    .input(z.object({ search: z.string().trim().max(200).optional() }))
    .output(z.object({ items: z.array(campaignStaffUserOptionSchema) })),

  /**
   * One campaign staffed at one or more institutions at once, each with its
   * own users, in one transaction: a refused institution or user refuses the
   * whole request.
   */
  create: oc
    .route({ method: 'POST', path: '/campaign-staff-assignments' })
    .errors(guarded)
    .input(
      z
        .object({
          campaignId: z.uuid(),
          status: z.enum(CAMPAIGN_STAFF_STATUSES).default('active'),
          startsAt: timestampSchema.optional(),
          endsAt: timestampSchema.nullable().default(null),
          institutions: z
            .array(z.object({ institutionId: z.uuid(), users: staffUsersSchema }))
            .min(1)
            .max(CAMPAIGN_STAFF_MAX_INSTITUTIONS),
        })
        .refine(windowIsOrdered, { path: ['endsAt'], message: 'must be after startsAt' }),
    )
    .output(z.object({ items: z.array(campaignStaffAssignmentSchema) })),

  /**
   * `users` replaces the whole set with each user's role; a field left out is
   * left as it is.
   */
  update: oc
    .route({ method: 'PATCH', path: '/campaign-staff-assignments/{id}' })
    .errors(guarded)
    .input(
      z
        .object({
          id: z.uuid(),
          status: z.enum(CAMPAIGN_STAFF_STATUSES).optional(),
          startsAt: timestampSchema.optional(),
          endsAt: timestampSchema.nullable().optional(),
          users: staffUsersSchema.optional(),
          confirmPendingDonations: z.boolean().optional(),
        })
        .refine(windowIsOrdered, { path: ['endsAt'], message: 'must be after startsAt' }),
    )
    .output(z.object({ assignment: campaignStaffAssignmentSchema })),

  /** How many pending or queued donations of the campaign wait at the institution. */
  impact: oc
    .route({ method: 'GET', path: '/campaign-staff-assignments/{id}/impact' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ pendingDonations: z.number().int().min(0) })),

  remove: oc
    .route({ method: 'DELETE', path: '/campaign-staff-assignments/{id}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), confirmPendingDonations: confirmationSchema }))
    .output(z.object({ id: z.uuid(), deleted: z.literal(true) })),

  /** The signed-in user's own assignments, whatever their status. */
  mine: oc
    .route({
      method: 'GET',
      path: '/my-campaign-staff-assignments',
      summary: 'The campaigns and institutions the signed-in user is assigned to',
    })
    .errors(guarded)
    .input(z.object({}))
    .output(z.object({ items: z.array(myCampaignAssignmentSchema) })),
};
